import { Col } from 'components';
import { getModerationLogs } from 'lib';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ContentModal from './ContentModal';
import LogsFilterBar from './LogsFilterBar';
import LogsTable from './LogsTable';
import Pagination from './Pagination';
import { AccessDenied, ErrorState } from './ui';

const DEFAULT_FILTERS = {
  from: '',
  to: '',
  action: 'all',
  category: 'all',
  contentType: 'all',
  inputType: 'all',
  userId: ''
};

const FILTER_KEYS = Object.keys(DEFAULT_FILTERS);
const DEFAULT_LIMIT = 20;
const TAB = 'logs';

const LogsTab = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // The URL is the single source of truth for the applied query.
  const applied = FILTER_KEYS.reduce((acc, key) => {
    acc[key] = searchParams.get(key) || DEFAULT_FILTERS[key];
    return acc;
  }, {});
  const page = Number(searchParams.get('page')) || 1;
  const limit = Number(searchParams.get('limit')) || DEFAULT_LIMIT;
  const appliedKey = searchParams.toString();

  // The free-text User ID is kept locally so typing feels instant, then written
  // to the URL (debounced) so it doesn't refetch on every keystroke.
  const [userIdInput, setUserIdInput] = useState(applied.userId);
  const userIdTimer = useRef(null);

  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [viewLogId, setViewLogId] = useState(null);
  const reqIdRef = useRef(0);

  const fetchLogs = async (f, p, lim) => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    const res = await getModerationLogs({ ...f, page: p, limit: lim });
    if (reqId !== reqIdRef.current) return; // superseded

    if (res?.status === 403) {
      setForbidden(true);
      setLoading(false);
      return;
    }
    if (res?.err || !res?.data) {
      setError(res?.err || 'Could not load moderation logs.');
      setLoading(false);
      return;
    }
    setLogs(res.data.logs || []);
    setPagination(res.pagination || null);
    setLoading(false);
  };

  // Refetch whenever the URL query changes (filters, page, limit).
  useEffect(() => {
    fetchLogs(applied, page, limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedKey]);

  // Keep the local User ID input in sync when the URL changes externally (Clear, back/forward).
  useEffect(() => {
    setUserIdInput(applied.userId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied.userId]);

  // Clean up the debounce timer on unmount.
  useEffect(() => () => userIdTimer.current && clearTimeout(userIdTimer.current), []);

  /** Merge updates into the current URL params, dropping empty / "all" values. */
  const writeParams = (updates) => {
    const next = { tab: TAB };
    searchParams.forEach((v, k) => {
      next[k] = v;
    });
    Object.entries(updates).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '' || v === 'all') delete next[k];
      else next[k] = String(v);
    });
    // replace: filter tweaks shouldn't flood the browser history.
    setSearchParams(next, { replace: true });
  };

  // Selects and date inputs write to the URL immediately (and reset to page 1).
  const handleFilterChange = (key, value) => writeParams({ [key]: value, page: 1 });

  // User ID is debounced so we don't refetch on every keystroke.
  const handleUserIdChange = (value) => {
    setUserIdInput(value);
    if (userIdTimer.current) clearTimeout(userIdTimer.current);
    userIdTimer.current = setTimeout(() => writeParams({ userId: value, page: 1 }), 400);
  };

  const handleClear = () => {
    if (userIdTimer.current) clearTimeout(userIdTimer.current);
    setUserIdInput('');
    setSearchParams({ tab: TAB });
  };

  const filterValues = { ...applied, userId: userIdInput };
  const total = pagination?.total ?? 0;
  const totalPages = total > 0 ? Math.max(1, Math.ceil(total / limit)) : 1;

  if (forbidden) return <AccessDenied />;

  return (
    <Col noFlex wid="100%">
      <LogsFilterBar
        filters={filterValues}
        onChange={handleFilterChange}
        onUserIdChange={handleUserIdChange}
        onClear={handleClear}
      />

      {error ? (
        <ErrorState message={error} onRetry={() => fetchLogs(applied, page, limit)} />
      ) : (
        <>
          <LogsTable rows={logs} loading={loading} onView={(log) => setViewLogId(log._id)} />
          {!loading && total > 0 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              pageSize={limit}
              total={total}
              onPageSizeChange={(size) => writeParams({ limit: size, page: 1 })}
              onPrev={() => writeParams({ page: page - 1 })}
              onNext={() => writeParams({ page: page + 1 })}
              onGoToPage={(p) => writeParams({ page: p })}
            />
          )}
        </>
      )}

      <ContentModal logId={viewLogId} onClose={() => setViewLogId(null)} />
    </Col>
  );
};

export default LogsTab;
